import { useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { ThresholdRowModel } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";

export default function useResolutionRateScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.resolution_rate,
  );

  const [thresholds, setThresholds] = useState<number[]>(() => THRESHOLDS.map(() => 0));
  const [weightHigh, setWeightHigh] = useState(60);
  const [weightMedium, setWeightMedium] = useState(60);
  const [weightLow, setWeightLow] = useState(40);

  useEffect(() => {
    const config = scoringConfig?.config;
    if (!config) return;

    const anchors = config.anchors;
    if (anchors?.length) {
      setThresholds(
        THRESHOLDS.map((t) => {
          const match = anchors.find((anchor) => anchor[1] === t.score);
          return match ? match[0] : 0;
        }),
      );
    }

    if (config.high_priority_weight !== undefined) setWeightHigh(config.high_priority_weight as number);
    if (config.medium_priority_weight !== undefined) setWeightMedium(config.medium_priority_weight as number);
    if (config.low_priority_weight !== undefined) setWeightLow(config.low_priority_weight as number);
  }, [scoringConfig]);

  const handleThresholdChange = (index: number, value: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSave = () => {
    saveScoringConfig(
      {
        anchors: THRESHOLDS.map((def, i) => [thresholds[i], def.score]).reverse(),
        high_priority_weight: weightHigh,
        medium_priority_weight: weightMedium,
        low_priority_weight: weightLow,
      },
      {
        onSuccess: () => {
          refetchReleaseStatus();
          displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
        },
        onError: () => {
          displayToast({ title: "Something went wrong", content: "Please try again." });
        },
      },
    );
  };

  const thresholdRows: ThresholdRowModel[] = THRESHOLDS.map((def, i) => ({
    score: def.score,
    label: def.label,
    value: thresholds[i],
    error: getValidationError(i, thresholds),
    onChange: (v: number) => handleThresholdChange(i, v),
    description: getDescription(def),
  }));

  return {
    thresholdRows,
    weightHigh,
    setWeightHigh,
    weightMedium,
    setWeightMedium,
    weightLow,
    setWeightLow,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

interface ThresholdConfig {
  score: number;
  default: number;
  label: string;
  range?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const THRESHOLDS: ThresholdConfig[] = [
  { score: 10, default: 200, label: "perfect", isFirst: true },
  { score: 8, default: 125, label: "excellent", range: "8.0 up to 9.9" },
  { score: 6, default: 70, label: "good", range: "6.0 up to 7.9" },
  { score: 4, default: 40, label: "warning", range: "4.0 up to 5.9" },
  { score: 2, default: 25, label: "concerning", range: "2.0 up to 3.9" },
  { score: 0, default: 10, label: "critical", range: "0.0 up to 1.9", isLast: true },
];

const formatScoreLabel = (score: number): string => (score === 10 ? "10" : `${score}.0`);

const getDescription = (def: ThresholdConfig): string => {
  if (def.isFirst) {
    return `You consider this 'Resolution rate', and all higher ones, as the threshold for a '${def.label}' score (${def.score}).   ←   System default: ${def.default}%.`;
  }
  if (def.isLast) {
    return `You consider this 'Resolution rate' as the lower threshold for a '${def.label}' score (${def.range}). Anything lower gets a 0 score.  ←   System default: ${def.default}%.`;
  }
  return `You consider this 'Resolution rate' as the lower threshold for a '${def.label}' score (${def.range}).  ←   System default: ${def.default}%.`;
};

const getValidationError = (index: number, values: number[]): string | undefined => {
  const value = values[index];
  const prevValue = index > 0 ? values[index - 1] : undefined;
  const nextValue = index < values.length - 1 ? values[index + 1] : undefined;

  const tooSmall = nextValue !== undefined && value <= nextValue;
  const tooBig = prevValue !== undefined && value >= prevValue;

  if (!tooSmall && !tooBig) return undefined;

  if (nextValue === undefined) {
    const prevScore = THRESHOLDS[index - 1].score;
    return `Must be less than ${prevValue}% (${formatScoreLabel(prevScore)} score threshold).`;
  }
  if (prevValue === undefined) {
    const nextScore = THRESHOLDS[index + 1].score;
    return `Must be greater than ${nextValue}% (${formatScoreLabel(nextScore)} score threshold).`;
  }

  const prevScore = THRESHOLDS[index - 1].score;
  const nextScore = THRESHOLDS[index + 1].score;
  return `Must be between ${nextValue}% (${formatScoreLabel(
    nextScore,
  )} score threshold) and ${prevValue}% (${formatScoreLabel(prevScore)} score threshold).`;
};
